import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QButton, QStack, QLink, useCurrentUser } from '@qualio/ui-components';
import { EventStep, AssociateDocumentPayload, AssociateDocumentResponse, User } from '../../../types';
import StepContainer from './StepContainer';
import StepHeader from './StepHeader';
import StepBody from './StepBody';
import EmptyStep from './EmptyStep';
import ContentStepMetadata from './ContentStepMetadata';
import AssigneesList from './AssigneesList';
import DigitalSignatureModal from '../../DigitalSignatureModal/DigitalSignatureModal';
import { DigitalSignature, DigitalSignatureInput } from '../../../types/digitalSignature';
import { useDocumentDetails, useAssociateDocument, useEventPermissions } from '../../../hooks';
import ReviewerList from './ReviewerList';
import StepStartTimeConstraintInfo from './StepStartTimeConstraintInfo';
import { isStepNotCompleted } from '../../../utils/eventUtils';
import { useRevertIssueStep } from '../../../hooks/useRevertIssueStep';
import { useFlags } from '../../../external/LaunchDarklyAdapter';
import AssignFormModal from '../../../views/EventStepForm/components/AssignFormModal';

type ContentStepProps = {
  isResolutionStepForApproval: boolean;
  step: EventStep;
  assignees: User[];
  refetch: () => void;
  lastUpdated: number | null;
  isResolutionStep?: boolean;
};

const ContentStep: React.FC<ContentStepProps> = ({
  isResolutionStepForApproval,
  step,
  assignees,
  refetch,
  lastUpdated,
  isResolutionStep = false,
}) => {
  const { t } = useTranslation();
  const { userId } = useCurrentUser();
  const isEventCollaboratorsEnabled = useFlags('eventCollaborators');
  const stepId = step.id;
  const eventId = step.issue_id;
  const documentId = step.document_id;
  const stepDocument = step.document;
  const { document, isDocumentLoading } = useDocumentDetails(documentId);
  const [isRevertStepModalOpen, setIsRevertStepModalOpen] = useState(false);
  const [isAssignStepModalOpen, setIsAssignStepModalOpen] = useState(false);
  const { canAddDocumentToStep, canRevertStep } = useEventPermissions();
  const audits = document?.audits || [];
  const stepStatus = step.status;
  const stepNotCompleted = isStepNotCompleted(stepStatus);
  const onAssociateDocumentSuccess = (
    data: AssociateDocumentResponse | undefined,
    { ownerId }: AssociateDocumentPayload,
  ) => {
    if (ownerId && ownerId !== userId) {
      setIsAssignStepModalOpen(false);
      refetch();
      return;
    }
    if (data) {
      window.location.href = `/workspace/documents/${data.id}?intent=edit`;
    }
  };

  const { associateDocumentMutate, isAssociateDocumentLoading } = useAssociateDocument(onAssociateDocumentSuccess);
  const { mutateAsync: revertIssueStepMutate } = useRevertIssueStep(step.issue_id, step.id);

  const handleRevertStep = useCallback(
    async (payload: DigitalSignature) => {
      await revertIssueStepMutate(payload);
      refetch();
    },
    [refetch, revertIssueStepMutate],
  );

  const ViewDocumentButton = ({ documentId }: { documentId: number | null }) => (
    <QLink href={`/workspace/documents/${documentId}`} isCrossMFE>
      <QButton variant="ghost" leftIcon="Search" data-metrics="step-header-view-button">
        {t('common.labels.view')}
      </QButton>
    </QLink>
  );

  const AssignStepButton = () => (
    <QButton
      variant="ghost"
      leftIcon="User"
      onClick={() => setIsAssignStepModalOpen(true)}
      isDisabled={!canAddDocumentToStep}
      data-metrics="content-step-assign-step-button"
      data-cy="assign-content-step"
    >
      {t('tasks.actions.assignStep')}
    </QButton>
  );

  const RevertStepButton = () => (
    <QButton
      variant="ghost"
      leftIcon="CornerUpLeft"
      onClick={() => setIsRevertStepModalOpen(true)}
      isDisabled={!canRevertStep || isResolutionStepForApproval}
      data-metrics="step-header-revert-step-button"
    >
      {t('common.labels.revert')}
    </QButton>
  );

  const revertStepModalInput: DigitalSignatureInput = {
    headingText: t('tasks.actions.revertStepCompletion'),
    subText: t('tasks.modals.signOffToRevertCompletion', { stepLabel: step.label }),
    submitButtonText: t('tasks.actions.revertStep'),
    successToastHeader: t('common.labels.stepReverted'),
    successToastDescription: t('tasks.modals.stepCompletionWasReverted', { stepLabel: step.label }),
  };

  return (
    <StepContainer>
      <StepHeader
        title={step.label}
        status={stepStatus}
        headerButton={
          documentId ? (
            <ViewDocumentButton documentId={documentId} />
          ) : (
            isEventCollaboratorsEnabled && stepNotCompleted && <AssignStepButton />
          )
        }
        revertStepButton={<RevertStepButton />}
      />
      <StepBody isLoading={isDocumentLoading}>
        <ContentStepMetadata document={stepDocument} />
        {!!step.start_time_constraint && stepDocument?.status_id === 'draft' && isResolutionStep && (
          <StepStartTimeConstraintInfo startTimeConstraint={step.start_time_constraint} lastUpdated={lastUpdated} />
        )}
        {documentId ? (
          document && (
            <QStack direction="row" spacing={20} m={4}>
              <AssigneesList
                label={t('common.labels.approvers')}
                assignees={document.audit_groups.approver_group ?? []}
                audits={audits}
              />
              <ReviewerList assignees={document.audit_groups.review_group ?? []} />
            </QStack>
          )
        ) : (
          <EmptyStep
            text={
              stepNotCompleted
                ? 'Add content and send it for review and approval when ready.'
                : 'There is no content associated with this step.'
            }
          >
            {stepNotCompleted && (
              <QButton
                variant="ghost"
                leftIcon="Edit3"
                onClick={() => associateDocumentMutate({ eventId, stepId })}
                isLoading={isAssociateDocumentLoading}
                isDisabled={!canAddDocumentToStep}
              >
                Add content
              </QButton>
            )}
          </EmptyStep>
        )}
      </StepBody>
      {isEventCollaboratorsEnabled && (
        <AssignFormModal
          isOpen={isAssignStepModalOpen}
          setIsOpen={setIsAssignStepModalOpen}
          onSave={(rawOwnerId) => associateDocumentMutate({ eventId, stepId, ownerId: Number(rawOwnerId) })}
          users={assignees}
          reassign={false}
          eventStepName={step.label}
          isApiLoading={isAssociateDocumentLoading}
          metricsPrefix="assign-content-step"
        />
      )}
      <DigitalSignatureModal
        isOpen={isRevertStepModalOpen}
        setIsOpen={setIsRevertStepModalOpen}
        onSave={handleRevertStep}
        inputTexts={revertStepModalInput}
        isCommentRequired={false}
      />
    </StepContainer>
  );
};

export default ContentStep;
